/* eslint-disable @typescript-eslint/naming-convention */

// Policy Name (API) → Policy UI Display Name
export const POLICY_DISPLAY_NAMES: Record<string, string> = {
  Default_Policy_SystemPromptEnforcement: "System Prompt Enforcement",
  Default_Policy_GeneralPromptAttackProtection: "General Prompt Attack Protection",
  Default_Policy_HarmfulContentProtection: "Harmful Content Protection",
  Default_Policy_PIISensitiveDataProtection: "PII & Sensitive Data Protection",
  Default_Policy_ContextGroundingValidation: "Context Grounding Validation",
  Default_Policy_ContentBiasProtection: "Content Bias Protection",
  Default_Policy_SkillsProtection: "Malicious Skills Protection",
  Default_Policy_OpenClaw_Detection: "OpenClaw Detection",
};

// Security Score Range → Label + Color
export const SCORE_COLOR_MAP = [
  {
    min: 89, max: 100, label: "Normal", color: "var(--score-color-normal, #34D7E6)",
  },
  {
    min: 70, max: 88, label: "Monitoring", color: "var(--score-color-monitoring, #EFC224)",
  },
  {
    min: 52, max: 69, label: "Medium", color: "var(--score-color-medium-6952, #F9852A)",
  },
  {
    min: 0, max: 51, label: "High", color: "var(--score-color-high-risk, #E33F3F)",
  },
] as const;

export function getScoreColor(score: number): { label: string; color: string } {
  const entry = SCORE_COLOR_MAP.find((e) => score >= e.min && score <= e.max);
  return entry ? { label: entry.label, color: entry.color } : { label: "N/A", color: "#999" };
}

// Risk Level (1-4) → Label + Color + Background
export const RISK_LEVEL_MAP: Record<number, { label: string; color: string; bg: string }> = {
  4: { label: "High", color: "var(--x-level-4, #DE2D2D)", bg: "rgba(222, 45, 45, 0.10)" },
  3: { label: "Medium", color: "var(--x-light-severity-8, #F07617)", bg: "rgba(240, 118, 23, 0.10)" },
  2: { label: "Low", color: "var(--x-severity-7, #EFC224)", bg: "rgba(239, 194, 36, 0.10)" },
  1: { label: "Minimal", color: "var(--x-light-severity-6, #00C9DB)", bg: "rgba(0, 201, 219, 0.10)" },
};

// Policy Name (API) → Risk Level (1-4)
export const POLICY_RISK_LEVEL: Record<string, number> = {
  Default_Policy_GeneralPromptAttackProtection: 4,
  Default_Policy_SystemPromptEnforcement: 3,
  Default_Policy_ContentBiasProtection: 2,
  Default_Policy_HarmfulContentProtection: 3,
  Default_Policy_PIISensitiveDataProtection: 2,
  Default_Policy_ContextGroundingValidation: 1,
  Default_Policy_SkillsProtection: 4,
  Default_Policy_OpenClaw_Detection: 1,
};

// Policy Name (API) → XTag Color Index (0=Cyan, 1=Red, 2=Purple, 3=Gray)
export const POLICY_TAG_COLOR_INDEX: Record<string, number> = {
  Default_Policy_SystemPromptEnforcement: 1, // Red
  Default_Policy_GeneralPromptAttackProtection: 1, // Red
  Default_Policy_HarmfulContentProtection: 1, // Red
  Default_Policy_PIISensitiveDataProtection: 2, // Purple
  Default_Policy_ContentBiasProtection: 2, // Purple
  Default_Policy_ContextGroundingValidation: 0, // Cyan
  Default_Policy_SkillsProtection: 1, // Red
  Default_Policy_OpenClaw_Detection: 0, // Cyan
};

/** Sort comparator: higher riskLevel first. */
export const sortByRiskLevelDesc = <T extends { riskLevel: number }>(a: T, b: T): number => b.riskLevel - a.riskLevel;

/**
 * API 請求參數 (Input)
 * 對應 GET /xecguard/v1/stats 的查詢參數
 */
export interface StatisticsParams {
  /** 統計期間的起始時間 (ISO 8601 UTC) */
  start_at: string;
  /** 統計期間的結束時間 (ISO 8601 UTC) */
  end_at: string;
  /** 指定時僅回傳對應 Service Token 的統計資料 */
  service_token_id?: string;
  /** 要回傳的最新違規筆數 (Default: 1000) */
  unsafe_count?: number;
  /** 要回傳的最新 API 錯誤筆數 (Default: 1000) */
  error_count?: number;
}

/**
 * API 回應中的請求統計數據 (Output)
 * 對應回應 JSON 中的 `request_statistics` 欄位
 * 包含流量統計、阻擋率與違規規則統計
 */
export interface RequestStatistics {
  service_token_id: string;
  start_at: string;
  end_at: string;
  /** 總請求數 */
  total_requests: number;
  /** 請求數 (request_count) */
  request_count: number;
  /** 不安全請求數 */
  request_unsafe_count: number;
  /** Session 總數 */
  total_session_count: number;
  /** 錯誤請求數 */
  request_error_count: number;
  /** 工具請求數 */
  tool_request_count: number;
  /** 被阻擋的請求數 */
  blocked_requests: number;
  /** 阻擋率 (0-1) */
  block_rate: number;
  /** 安全分數 (0-100) */
  security_score: number;
  /** 各規則違規次數統計 (violation_type_counts) */
  violation_type_counts: Record<string, number>;
  /** 各規則違規次數統計 (legacy) */
  violated_types: Record<string, number>;
  /** 依風險等級分類的阻擋請求數 (key: 1-4) */
  blocked_requests_by_max_risk?: Record<string, number>;
}

export interface PerformanceMetrics {
  avg_response_time_ms: number;
  max_response_time_ms: number;
  p95_response_time_ms: number;
  p99_response_time_ms: number;
}

export interface MaliciousRequest {
  service_token_id: string;
  trace_id: string;
  route: string;
  violated_types: string[];
  violated_policy_names: string[];
  timestamp: string;
  latency_ms?: number;
}

export interface ApiError {
  service_token_id: string;
  trace_id: string;
  route: string;
  status_code: number;
  error_code: string;
  timestamp: string;
}

export interface StatisticsResponse {
  request_statistics: RequestStatistics;
  request_latency_metrics: PerformanceMetrics;
  unsafe_requests: MaliciousRequest[];
  api_errors: ApiError[];
}
